import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClientService } from "@app/client/clientService";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { ProjectPerformanceService } from "@app/operations/project-performance/projectPerformanceService";

export interface ProjectPerformanceFiltersProps {
  availableJobTypes: string[];
  isLoading: boolean;
  onJobTypesChanged: (jobTypes: string[]) => void;
  onProjectSelected: (projectId: number | undefined) => void;
}

function buildJobTypeItems(jobTypes: string[]): DropdownItem<string>[] {
  return jobTypes.map(jobType => ({ key: jobType, name: jobType }));
}

export const ProjectPerformanceFilters: FC<ProjectPerformanceFiltersProps> = ({ availableJobTypes, isLoading, onJobTypesChanged, onProjectSelected }) => {
  const clientService = useRef(new ClientService()).current;
  const projectPerformanceService = useRef(new ProjectPerformanceService()).current;

  const [selectedClientId, setSelectedClientId] = useState<number | undefined>();
  const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>();
  const [selectedJobTypes, setSelectedJobTypes] = useState<DropdownItem<string>[]>([]);

  const { data: clients = [] } = useQuery({
    queryFn: () => clientService.getActiveClientsList(),
    queryKey: ["project-performance-clients"],
    staleTime: 5 * 60 * 1000,
  });

  const { data: projects = [], isFetching: isFetchingProjects } = useQuery({
    enabled: !!selectedClientId,
    queryFn: ({ signal }) => projectPerformanceService.getProjectsByClient(selectedClientId!, signal),
    queryKey: ["project-performance", "projects", selectedClientId],
  });


  const jobTypeItems = useMemo(() => buildJobTypeItems(availableJobTypes), [availableJobTypes]);
  const previousJobTypeKeysRef = useRef<string[]>([]);

  useEffect(() => {
    const currentKeys = jobTypeItems.map(item => item.key);
    const previousKeys = previousJobTypeKeysRef.current;
    const isUnchanged = currentKeys.length === previousKeys.length && currentKeys.every((key, i) => key === previousKeys[i]);
    if (isUnchanged) {
      return;
    }
    previousJobTypeKeysRef.current = currentKeys;
    setSelectedJobTypes(jobTypeItems);
    onJobTypesChanged(currentKeys);
  }, [jobTypeItems, onJobTypesChanged]);

  const handleClientChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedClientId(event.target.value ? Number(event.target.value) : undefined);
    setSelectedProjectId(undefined);
    onProjectSelected(undefined);
  };

  const handleProjectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const projectId = Number(event.target.value);
    setSelectedProjectId(projectId);
    onProjectSelected(projectId);
  };

  const handleJobTypesChange = (items: DropdownItem<string>[]) => {
    setSelectedJobTypes(items);
    onJobTypesChanged(items.map(item => item.key));
  };

  return (
    <div className="d-flex flex-wrap align-items-end gap-3 mb-4 project-performance__filters">
      <QESSelect
        disabled={isLoading}
        label="Client"
        onChange={handleClientChange}
        placeholder="Select Client"
        testId="project-performance__client-select"
        value={selectedClientId?.toString() ?? ""}
      >
        {clients.map(client => (
          <option key={client.clientId} value={client.clientId}>{client.name}</option>
        ))}
      </QESSelect>

      <QESSelect
        disabled={!selectedClientId || isFetchingProjects || isLoading}
        label="Project"
        onChange={handleProjectChange}
        placeholder="Select Project"
        testId="project-performance__project-select"
        value={selectedProjectId?.toString() ?? ""}
      >
        {projects.map(project => (
          <option key={project.projectId} value={project.projectId}>{project.projectName}</option>
        ))}
      </QESSelect>

      <MultiselectDropdown
        customWidth
        disabled={availableJobTypes.length === 0 || isLoading}
        items={jobTypeItems}
        label="Job Types"
        onSelectionChanged={handleJobTypesChange}
        selectedItems={selectedJobTypes}
        testId="project-performance__job-type-multiselect"
      />
    </div>
  );
};

export default ProjectPerformanceFilters;
